/**
 * LanguagePreferencePanel — Iter314
 *
 * Dedicated language settings surface with flag chips + live preview + explicit
 * save to backend. Complements the compact <LangToggle /> in the header by
 * offering a proper preview of representative UI copy before committing.
 */
import { useState, useMemo } from "react";
import { Check, Globe, Save, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "../i18n/LangContext";
import { TRANSLATIONS } from "../i18n/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGS = [
  { code: "en", name: "English",    native: "English",    flag: "🇺🇸" },
  { code: "pt", name: "Portuguese", native: "Português",  flag: "🇧🇷" },
  { code: "es", name: "Spanish",    native: "Español",    flag: "🇪🇸" },
];

// Representative keys the user actually reads every day — perfect for a
// side-by-side preview before committing.
const PREVIEW_KEYS = [
  "sidebar.dashboard",
  "sidebar.lead_sourcing",
  "sidebar.leads_map",
  "sidebar.crm",
  "sidebar.buying_signals",
  "vsm.route.optimizer",
  "vsm.privacy.city",
  "claire.typing",
];

export default function LanguagePreferencePanel() {
  const { lang, setLang, t } = useLang();
  const [candidate, setCandidate] = useState(lang);
  const [saving, setSaving] = useState(false);

  const preview = useMemo(() => {
    const dict = TRANSLATIONS[candidate] || TRANSLATIONS.en;
    return PREVIEW_KEYS.map((k) => ({
      key: k,
      value: dict[k] ?? TRANSLATIONS.en[k] ?? k,
    }));
  }, [candidate]);

  const hasChange = candidate !== lang;

  const onSave = async () => {
    if (!hasChange) return;
    setSaving(true);
    try {
      // Update client state — LangContext's own effect will PATCH the backend.
      // But we also fire the PATCH explicitly here so we can surface success
      // and errors to the user with a toast.
      await axios.patch(`${API}/auth/me/preferences/lang`,
        { lang: candidate }, { withCredentials: true });
      setLang(candidate);
      toast.success(t("lang.saved") || "Language preference saved");
    } catch (err) {
      // Even if the backend fails, apply locally so the UI updates.
      setLang(candidate);
      toast.warning(t("lang.saved_local") || "Saved locally (backend unavailable)");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-4"
         data-testid="language-preference-panel">
      <div className="flex items-center gap-2 mb-3">
        <Globe className="w-4 h-4 text-sky-300" />
        <h3 className="text-[12px] font-extrabold uppercase tracking-widest text-white">
          {t("settings.language.title") || "Language / Idioma"}
        </h3>
      </div>
      <p className="text-[11px] text-slate-400 mb-4 leading-relaxed">
        {t("settings.language.description")
          || "Pick the language you want to use across the app. Preference syncs across devices."}
      </p>

      {/* Flag chips */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {LANGS.map((L) => {
          const active = L.code === candidate;
          const isCurrent = L.code === lang;
          return (
            <button key={L.code} type="button"
              onClick={() => setCandidate(L.code)}
              data-testid={`language-chip-${L.code}`}
              className={`relative rounded-lg border p-3 flex flex-col items-center gap-1.5 transition-all ${
                active
                  ? "border-sky-400/70 bg-sky-500/15 shadow-lg shadow-sky-500/10 scale-[1.02]"
                  : "border-white/10 bg-black/30 hover:border-white/25"}`}>
              <span className="text-2xl leading-none">{L.flag}</span>
              <span className="text-[11.5px] font-bold text-white">{L.native}</span>
              <span className="text-[9px] uppercase tracking-widest text-slate-500">{L.name}</span>
              {isCurrent && (
                <span className="absolute top-1 right-1 inline-flex items-center gap-0.5 text-[8.5px] font-bold text-emerald-300 bg-emerald-500/15 border border-emerald-500/40 rounded px-1 py-0.5">
                  <Check className="w-2 h-2" /> {t("settings.language.current") || "Current"}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Live preview */}
      <div className="rounded-lg border border-white/10 bg-black/30 p-3 mb-4"
           data-testid="language-preview">
        <div className="text-[9.5px] uppercase tracking-widest font-bold text-slate-500 mb-2">
          {t("settings.language.preview") || "Preview"}
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
          {preview.map((row) => (
            <div key={row.key} className="flex items-center gap-2">
              <span className="text-slate-500 text-[9.5px] font-mono truncate w-[100px]"
                    title={row.key}>{row.key.replace(/^[a-z_]+\./, "")}</span>
              <span className="text-white truncate">{row.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Save button */}
      <div className="flex items-center gap-2 justify-end">
        {hasChange && (
          <span className="text-[10.5px] text-amber-300 mr-auto italic">
            {t("settings.language.unsaved") || "Unsaved change — click Save to apply"}
          </span>
        )}
        <button type="button"
          onClick={onSave}
          disabled={!hasChange || saving}
          data-testid="language-save"
          className={`h-8 px-3 rounded-md text-[11.5px] font-bold inline-flex items-center gap-1.5 border ${
            !hasChange || saving
              ? "border-white/10 bg-black/20 text-slate-600 cursor-not-allowed"
              : "border-sky-400/70 bg-sky-500/20 text-sky-100 hover:bg-sky-500/30"}`}>
          {saving
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("btn.saving") || "Saving…"}</>
            : <><Save className="w-3.5 h-3.5" /> {t("btn.save") || "Save"}</>}
        </button>
      </div>
    </div>
  );
}
